import React from 'react';
import { FiAlertCircle } from 'react-icons/fi';

export const PageHero = ({ eyebrow, title, description, actions, stats, aside, className = '' }) => (
  <section className={`content-width py-10 sm:py-14 ${className}`}>
    <div className="hero-grid glass-panel px-6 py-8 sm:px-10 sm:py-12">
      <div className="space-y-6">
        {eyebrow ? <span className="eyebrow">{eyebrow}</span> : null}
        <div className="space-y-4">
          <h1 className="section-title max-w-3xl">{title}</h1>
          {description ? <p className="section-copy max-w-2xl">{description}</p> : null}
        </div>
        {actions ? <div className="flex flex-wrap gap-3">{actions}</div> : null}
        {stats ? <div className="grid gap-3 sm:grid-cols-3">{stats}</div> : null}
      </div>
      {aside ? <div>{aside}</div> : null}
    </div>
  </section>
);

export const SectionHeading = ({ eyebrow, title, description, action }) => (
  <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
    <div className="space-y-3">
      {eyebrow ? <span className="eyebrow">{eyebrow}</span> : null}
      <div className="space-y-2">
        <h2 className="text-2xl font-semibold text-slate-900 sm:text-3xl">{title}</h2>
        {description ? <p className="max-w-2xl text-sm leading-7 text-slate-600 sm:text-base">{description}</p> : null}
      </div>
    </div>
    {action ? <div>{action}</div> : null}
  </div>
);

export const MetricCard = ({ label, value, hint, icon, tone = 'accent' }) => {
  const toneClass =
    tone === 'secondary'
      ? 'bg-orange-100 text-orange-700'
      : tone === 'neutral'
        ? 'bg-stone-200 text-stone-700'
        : 'bg-teal-100 text-teal-700';

  return (
    <div className="metric-card">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm uppercase tracking-[0.18em] text-stone-500">{label}</p>
          <p className="metric-value">{value}</p>
          {hint ? <p className="mt-2 text-sm text-slate-500">{hint}</p> : null}
        </div>
        {icon ? <div className={`rounded-2xl p-3 ${toneClass}`}>{icon}</div> : null}
      </div>
    </div>
  );
};

export const LoadingState = ({ label = 'Loading...' }) => (
  <div className="content-width py-16">
    <div className="soft-panel flex min-h-[260px] flex-col items-center justify-center gap-4">
      <div className="loading-orbit" />
      <p className="text-sm uppercase tracking-[0.2em] text-stone-500">{label}</p>
    </div>
  </div>
);

export const ErrorState = ({ title = 'Something went wrong', message, action }) => (
  <div className="content-width py-16">
    <div className="soft-panel flex min-h-[260px] flex-col items-center justify-center gap-4 px-6 text-center">
      <div className="rounded-full bg-red-100 p-4 text-red-700">
        <FiAlertCircle className="h-8 w-8" />
      </div>
      <div className="space-y-2">
        <h2 className="text-2xl font-semibold text-slate-900">{title}</h2>
        {message ? <p className="max-w-lg text-sm text-slate-600 sm:text-base">{message}</p> : null}
      </div>
      {action ? <div>{action}</div> : null}
    </div>
  </div>
);

export const EmptyState = ({ title, description, action }) => (
  <div className="empty-state">
    <h3 className="text-2xl font-semibold text-slate-900">{title}</h3>
    {description ? <p className="mx-auto mt-3 max-w-xl text-sm leading-7 text-slate-600 sm:text-base">{description}</p> : null}
    {action ? <div className="mt-6">{action}</div> : null}
  </div>
);
